import * as React from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

interface SelectContextValue {
  value: string;
  label: string;
  onValueChange: (value: string, label: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  registerItem: (value: string, label: string) => void;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelectContext() {
  const context = React.useContext(SelectContext);
  if (!context) {
    throw new Error('Select components must be used within a Select');
  }
  return context;
}

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  children: React.ReactNode;
}

function Select({ value: controlledValue, onValueChange, defaultValue = '', children }: SelectProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const [label, setLabel] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const itemLabels = React.useRef<Map<string, string>>(new Map());

  const value = controlledValue ?? internalValue;

  const registerItem = React.useCallback((itemValue: string, itemLabel: string) => {
    itemLabels.current.set(itemValue, itemLabel);
    // If this is the currently selected value, update the label
    if (itemValue === value && itemLabel !== label) {
      setLabel(itemLabel);
    }
  }, [value, label]);

  // Update label when value changes externally
  React.useEffect(() => {
    const storedLabel = itemLabels.current.get(value);
    if (storedLabel && storedLabel !== label) {
      setLabel(storedLabel);
    }
  }, [value, label]);

  const handleValueChange = (newValue: string, newLabel: string) => {
    setInternalValue(newValue);
    setLabel(newLabel);
    onValueChange?.(newValue);
  };

  return (
    <SelectContext.Provider value={{ value, label, onValueChange: handleValueChange, open, setOpen, registerItem }}>
      <div className="relative">{children}</div>
    </SelectContext.Provider>
  );
}

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { open, setOpen } = useSelectContext();

    return (
      <button
        ref={ref}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(!open)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm ring-offset-[hsl(var(--background))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: string;
}

function SelectValue({ placeholder }: SelectValueProps) {
  const { value, label } = useSelectContext();
  const displayText = label || value;
  return <span className={cn('truncate', displayText ? '' : 'text-[hsl(var(--muted-foreground))]')}>{displayText || placeholder}</span>;
}

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

function SelectContent({ children, className }: SelectContentProps) {
  const { open, setOpen } = useSelectContext();
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: Event) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('pointerdown', handleClickOutside);
      return () => document.removeEventListener('pointerdown', handleClickOutside);
    }
  }, [open, setOpen]);

  // Always render children so items can register their labels,
  // but only show the dropdown visually when open
  return (
    <div
      ref={ref}
      role="listbox"
      className={cn(
        'absolute top-full left-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-1 shadow-md',
        !open && 'hidden',
        className
      )}
    >
      {children}
    </div>
  );
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

function SelectItem({ value: itemValue, children, className }: SelectItemProps) {
  const { value, onValueChange, setOpen, registerItem } = useSelectContext();
  const isSelected = value === itemValue;
  const ref = React.useRef<HTMLDivElement>(null);

  // Register the item's text label so SelectValue can display it
  React.useEffect(() => {
    const textContent = ref.current?.textContent || '';
    if (textContent) {
      registerItem(itemValue, textContent);
    }
  }, [itemValue, registerItem, children]);

  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isSelected}
      onClick={() => {
        const textContent = ref.current?.textContent || '';
        onValueChange(itemValue, textContent);
        setOpen(false);
      }}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none hover:bg-[hsl(var(--muted))] focus:bg-[hsl(var(--muted))]',
        isSelected && 'bg-[hsl(var(--muted))]',
        className
      )}
    >
      {children}
    </div>
  );
}

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
